import FormItemInput from "./form-item/form-item-input";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { useEffect, useRef, useState } from "react";
import { Button, Tooltip } from "antd";
import { $api, type components } from "../helper";
import { tokenMock } from "../helper/mock";
import { Icon } from "@iconify/react";
import { onRemove } from "../helper/method";
import { useNotify } from "../helper/notify";

export default function MessageBar({
  disabled,
  ticketId,
  refetch,
  setTicketDetail,
}: {
  disabled: boolean;
  ticketId: string;
  refetch: any;
  setTicketDetail: React.Dispatch<
    React.SetStateAction<
      | {
          ticket_detail: components["schemas"]["SiteTicketDetailTicketItem"];
          user_detail: components["schemas"]["SiteTicketDetailUserItem"];
          file_detail: components["schemas"]["SiteTicketDetailFileItem"][];
          messages: components["schemas"]["SiteTicketDetailMessageItem"][];
        }
      | undefined
    >
  >;
}) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);

  useEffect(() => {
    if (!editorRef.current) return;

    if (quillRef.current) return;

    quillRef.current = new Quill(editorRef.current, {
      theme: "snow",
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ direction: "rtl" }],
          [{ align: [] }],
          ["link"],
          ["clean"],
        ],
      },
    });
  }, []);
  const [file, setFile] = useState<
    {
      file: File | undefined;
      fileUrl: string;
      id: string;
    }[]
  >([]);
  const [message, setMessage] = useState("");
  const notify = useNotify();
  const { mutate, isPending } = $api.useMutation(
    "post",
    "/site-api/v1/ticket-message/",
  );
  const [focus, setFocus] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (quillRef.current) {
      quillRef.current.format("align", "right");
      quillRef.current.on("text-change", () => {
        setMessage(
          Number(quillRef.current?.editor.delta.ops[0].insert?.length) <= 1
            ? ""
            : (quillRef.current.root.innerHTML ?? ""),
        );
      });

      const toolbar = document.querySelector(".ql-toolbar");
      if (toolbar) toolbar.style.display = "none";
    }
  }, []);
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        focus &&
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target)
      ) {
        setFocus(false);
      }
    }
    if (focus) {
      const toolbar = document.querySelector(".ql-toolbar");
      if (toolbar) toolbar.style.display = "block";
    }
    if (!focus) {
      const toolbar = document.querySelector(".ql-toolbar");
      if (toolbar) toolbar.style.display = "none";
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [focus]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles = Array.from(files).map((f) => ({
      file: f,
      id: `${f.name}-${Date.now()}`,
      fileUrl: URL.createObjectURL(f),
    }));

    setFile((prev) => [...prev, ...newFiles]);
  };

  const onfinish = () => {
    if (file.length > 5) {
      notify.error("تعداد فایل بیشتر از حد مجاز است");
      return;
    }
    const formData = new FormData();
    formData.append("message", message);
    formData.append("ticket_u_id", ticketId);
    file.forEach((element) => {
      if (element.file) {
        formData.append("files", element.file);
      }
    });
    mutate(
      {
        body: formData,
        headers: {
          Authorization: tokenMock,
          lang: "fa",
        },
      },
      {
        onSuccess: (data) => {
          notify.success(data.message);
          setMessage("");
          setFile([]);
          setFocus(false);
          if (quillRef.current) {
            quillRef.current.setContents([]);
          }
          refetch.mutate(
            {
              params: {
                query: {
                  ticket_u_id: ticketId,
                },
              },
              headers: {
                Authorization: tokenMock,
                lang: "fa",
              },
            },
            {
              onSuccess: (data) => {
                setTicketDetail(data.data);
              },
            },
          );
        },
      },
    );
  };

  return (
    <div
      id="message-bar"
      className="absolute bg-white border-grayscale-200 bottom-0 left-0 right-0 w-full py-3 px-4 border-t"
    >
      <div ref={wrapperRef}>
        <div
          dir="auto"
          className={`${
            focus ? "h-36! opacity-100 visible" : "h-0! opacity-0! invisible"
          } w-full border-r border-l border-b border-grayscale-200 rounded-br-xl rounded-bl-xl`}
          lang="fa"
          ref={editorRef}
        />
        <FormItemInput
          className={`${focus ? "hidden" : "flex"} inp w-full! &:w-full!`}
          inputProps={{
            variant: "borderless",
            className: "w-full!",
            placeholder: "پیام...",
            onFocus: () => setFocus(true),
            disabled: disabled,
          }}
        />
        {focus ? (
          <div className="flex gap-3 items-center">
            <Button
              onClick={() => onfinish()}
              className="mt-4! bg-transparent! text-grayscale-950! text-2xl! w-fit! p-0! h-fit!"
              type="primary"
              loading={isPending}
              icon={<Icon icon={"ic:baseline-send"} />}
            />
            <label htmlFor="file" className="cursor-pointer">
              <input
                type="file"
                disabled={isPending}
                id="file"
                name="file"
                accept="image/* , application/pdf"
                className="hidden"
                onChange={handleChange}
              />
              <span>
                <Icon
                  icon="ic:twotone-attach-file"
                  className="text-2xl rotate-45 mt-2!"
                />
              </span>
            </label>
            {file.map((e, i) => (
              <Tooltip title={e.id} key={i + "file-preview"}>
                <div className="mt-2 relative">
                  <button
                    disabled={isPending}
                    onClick={() => onRemove(e.id, setFile)}
                    className="absolute -left-1.5 cursor-pointer -top-1.5 text-xl!"
                  >
                    <Icon icon={"ic:baseline-cancel"} />
                  </button>
                  <div className="bg-grayscale-200 rounded-2xl w-11 flex-center h-11">
                    <Icon
                      icon={"solar:file-bold-duotone"}
                      className="text-3xl"
                    />
                  </div>
                </div>
              </Tooltip>
            ))}
          </div>
        ) : (
          ""
        )}
      </div>
    </div>
  );
}
